import type { AxiosInstance } from 'axios';
import type { TranslateFn, U2000ApiErrorResult } from '../u2000Core/axios/U2000HandleApiError';
import type { U2000SearchPayload } from '../u2000Core/components/tables/basic/U2000BasicTable';
import { u3204ValidateAttachments } from './utils/u3204AttachmentUtils';
import type { U3202ModelCapabilities } from '../u3202AiProvider/utils/U3202CapabilitiesTypes';
import type { U3203MissionCategory } from '../u3203AiMission/U3203Service';

const LOGICALUNIT_URL = 'U3204';

type LogicalUnitEndpointsType = {
  INITLU: string;
  ACCESSIBLE_MISSIONS: string;
  QUOTA_STATUS: string;
  SEARCH_CONVERSATIONS: string;
  CREATE_CONVERSATION: string;
  GET_CONVERSATION: (id: string | number) => string;
  UPDATE_CONVERSATION: (id: string | number) => string;
  DELETE_CONVERSATION: (id: string | number) => string;
  REORDER_PINNED_CONVERSATIONS: string;
  SEND_MESSAGE: (id: string | number) => string;
  PRODUCT_HELP_SOURCES: string;
  PRODUCT_HELP_REINDEX: string;
};

const LogicalUnitEndpoints: LogicalUnitEndpointsType = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  ACCESSIBLE_MISSIONS: `${LOGICALUNIT_URL}/accessibleMissions`,
  QUOTA_STATUS: `${LOGICALUNIT_URL}/quotaStatus`,
  SEARCH_CONVERSATIONS: `${LOGICALUNIT_URL}/conversations/search`,
  CREATE_CONVERSATION: `${LOGICALUNIT_URL}/conversations/create`,
  GET_CONVERSATION: (id: string | number) => `${LOGICALUNIT_URL}/conversations/${id}`,
  UPDATE_CONVERSATION: (id: string | number) => `${LOGICALUNIT_URL}/conversations/update/${id}`,
  DELETE_CONVERSATION: (id: string | number) => `${LOGICALUNIT_URL}/conversations/delete/${id}`,
  REORDER_PINNED_CONVERSATIONS: `${LOGICALUNIT_URL}/conversations/reorder-pinned`,
  SEND_MESSAGE: (id: string | number) => `${LOGICALUNIT_URL}/conversations/${id}/send`,
  PRODUCT_HELP_SOURCES: `${LOGICALUNIT_URL}/productHelp/sources`,
  PRODUCT_HELP_REINDEX: `${LOGICALUNIT_URL}/productHelp/reindex`,
};

const U2200_JOB_GET = (id: string) => `U2200/jobs/${id}`;

/** Job type U2200 pour reindex PRODUCT_HELP (BL-0203). */
export const U3204_PRODUCT_HELP_REINDEX_JOB = 'U3204_PRODUCT_HELP_REINDEX';

export type U3204U2200JobStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface U3204ProductHelpSource {
  id: string;
  logicalUnitCode: string;
  sourcePath: string;
  contentHash: string;
  revision: number;
  indexedAt: string | null;
  chunkCount: number;
}

export interface U3204ProductHelpReindexBody {
  logicalUnitCode?: string;
}

export interface U3204ProductHelpIndexCounters {
  discovered?: number;
  indexed?: number;
  skipped?: number;
  errors?: number;
  chunksCreated?: number;
  embeddingsUpdated?: number;
}

export interface U3204U2200Job {
  id: string;
  jobType: string;
  status: U3204U2200JobStatus;
  result?: {
    success?: boolean;
    message?: string;
    data?: U3204ProductHelpIndexCounters;
  } | null;
  errorMessage?: string | null;
}

export type U3204ConversationStatus = 'active' | 'archived';

export type U3204MessageRole = 'user' | 'assistant' | 'system';
export type U3204GenerationStatus = 'pending' | 'completed' | 'failed';
export type U3204StreamState = 'idle' | 'streaming' | 'error';
export type U3204ToolActivityStatus = 'running' | 'success' | 'error';

export interface U3204ToolActivityItem {
  toolName: string;
  status: U3204ToolActivityStatus;
}

export interface U3204UserGroupRef {
  id: string;
  code: string;
  name: string;
}

export interface U3204InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  logicalUnitCode: string;
  allowedMimeTypes: string[];
  maxAttachmentBytes: number;
  maxAttachmentsPerMessage: number;
  messageRoles: U3204MessageRole[];
  showThinking: boolean;
  hasUserGroup: boolean;
  userGroup?: U3204UserGroupRef | null;
}

export interface U3204ProviderRef {
  id: string;
  code: string;
  name: string;
}

export interface U3204QuotaAxis {
  usageTokens: number;
  quotaMonthlyTokens: number | null;
  usagePercent: number | null;
}

export interface U3204QuotaStatus {
  provider: U3204ProviderRef;
  period: {
    start: string;
    timezone: string;
  };
  user: U3204QuotaAxis;
  group: U3204QuotaAxis;
  blocked: boolean;
  blockReason: 'user' | 'group' | null;
}

export interface U3204ModelRef {
  id: string;
  code: string;
  name: string;
  capabilities?: U3202ModelCapabilities;
}

function u3204NormalizeModelRef(model: U3204ModelRef | null | undefined): U3204ModelRef | null {
  if (!model?.id) return null;
  return {
    id: model.id,
    code: model.code,
    name: model.name,
    capabilities: model.capabilities ?? {},
  };
}

export interface U3204DefaultPromptRef {
  id: string;
  labelFr: string;
  labelEn: string;
  promptFr: string;
  promptEn: string;
  sortOrder: number;
}

export interface U3204MissionRef {
  id: string;
  code: string;
  title: string;
  defaultProvider: U3204ProviderRef | null;
  defaultModel: U3204ModelRef | null;
  defaultPrompts: U3204DefaultPromptRef[];
}

export function u3204NormalizeMissionRef(
  mission: Pick<U3204MissionRef, 'id' | 'code' | 'title'> & {
    defaultProvider?: U3204ProviderRef | null;
    defaultModel?: U3204ModelRef | null;
    defaultPrompts?: U3204DefaultPromptRef[];
  },
): U3204MissionRef {
  return {
    id: mission.id,
    code: mission.code,
    title: mission.title,
    defaultProvider: mission.defaultProvider ?? null,
    defaultModel: u3204NormalizeModelRef(mission.defaultModel),
    defaultPrompts: [...(mission.defaultPrompts ?? [])].sort((a, b) => a.sortOrder - b.sortOrder),
  };
}

export interface U3204Attachment {
  id: string;
  attachmentType: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  textContent?: string | null;
  /** URL de téléchargement (exports générés, ex. Excel BL-0166 / Word BL-0141 / PDF BL-0142) */
  fileUrl?: string | null;
}

/** Fichier export généré prêt au téléchargement (Excel BL-0166 / Word BL-0141 / PDF BL-0142) */
export interface U3204ExportFile {
  fileUrl: string;
  fileName: string;
  fileMimeType: string;
}

/** Citation doc produit (RAG) — BL-0200 */
export interface U3204MessageSourceProduct {
  kind: 'product';
  chunkId?: string;
  logicalUnitCode: string;
  sectionPath: string;
  sectionTitle: string;
  url: string;
  score?: number;
}

/** Citation doc org (RAG) — BL-0219 */
export interface U3204MessageSourceOrg {
  kind: 'org';
  sourceId: string;
  chunkId?: string;
  logicalUnitCode: string;
  sectionPath: string;
  sectionTitle: string;
  displayName?: string;
  url: string;
  score?: number;
}

/** Citation RAG — union product | org (BL-0200 / BL-0219) */
export type U3204MessageSource = U3204MessageSourceProduct | U3204MessageSourceOrg;

export interface U3204Message {
  id: string;
  role: U3204MessageRole;
  content: string;
  thinkingContent?: string | null;
  toolActivity?: U3204ToolActivityItem[];
  generationStatus?: U3204GenerationStatus;
  inputTokens?: number | null;
  outputTokens?: number | null;
  attachments?: U3204Attachment[];
  /** Export Excel/Word/PDF prêt — carte téléchargement dans le fil (BL-0166 / BL-0141 / BL-0142) */
  exportFile?: U3204ExportFile | null;
  /** Sources RAG (docs HTML) — BL-0200 ; absent/null/[] = pas de bloc Sources */
  sources?: U3204MessageSource[] | null;
}

export interface U3204ConversationSearchRow {
  id: string;
  title: string | null;
  status: string;
  mission?: U3204MissionRef | null;
  createdAt: string;
  lastModifiedAt: string;
  version?: number;
  pinned: boolean;
  pinnedAt: string | null;
  pinnedOrder: number | null;
  titleAutoGenerated?: boolean;
}

/** Ligne brute renvoyée par POST /U3204/conversations/search (missionCode/missionTitle ou mission imbriquée). */
export type U3204ConversationSearchRowApi = Omit<
  U3204ConversationSearchRow,
  'lastModifiedAt' | 'pinned' | 'pinnedAt' | 'pinnedOrder'
> & {
  lastModifiedAt?: string | null;
  updatedAt?: string | null;
  pinned?: boolean;
  pinnedAt?: string | null;
  pinnedOrder?: number | null;
  titleAutoGenerated?: boolean;
  missionId?: string;
  missionCode?: string;
  missionTitle?: string;
};

export function u3204MapConversationSearchRow(row: U3204ConversationSearchRowApi): U3204ConversationSearchRow {
  const rawMission =
    row.mission ??
    (row.missionId && row.missionCode && row.missionTitle
      ? { id: row.missionId, code: row.missionCode, title: row.missionTitle }
      : row.missionCode && row.missionTitle
        ? { id: row.missionId ?? '', code: row.missionCode, title: row.missionTitle }
        : null);
  const mission = rawMission ? u3204NormalizeMissionRef(rawMission) : null;

  const lastModifiedAt = row.lastModifiedAt ?? row.updatedAt ?? row.createdAt;

  return {
    id: row.id,
    title: row.title ?? null,
    status: row.status,
    mission,
    createdAt: row.createdAt,
    lastModifiedAt,
    version: row.version,
    pinned: row.pinned ?? false,
    pinnedAt: row.pinnedAt ?? null,
    pinnedOrder: row.pinnedOrder ?? null,
    titleAutoGenerated: row.titleAutoGenerated,
  };
}

export function u3204MapConversationSearchRows(rows: U3204ConversationSearchRowApi[]): U3204ConversationSearchRow[] {
  return rows.map(u3204MapConversationSearchRow);
}

export interface U3204Conversation {
  id: string;
  title: string | null;
  status: string;
  mission: U3204MissionRef;
  messages: U3204Message[];
  version: number;
  createdAt?: string;
  pinned?: boolean;
  pinnedAt?: string | null;
  pinnedOrder?: number | null;
  titleAutoGenerated?: boolean;
}

export interface U3204CreateConversationBody {
  missionId: string;
  title?: string | null;
}

export interface U3204CreateConversationResult {
  id: string;
  title: string | null;
  status: string;
  mission: U3204MissionRef;
  version: number;
  createdAt: string;
  lastModifiedAt?: string;
  pinned?: boolean;
  pinnedAt?: string | null;
  pinnedOrder?: number | null;
  titleAutoGenerated?: boolean;
}

export interface U3204ConversationUpdateBody {
  id: string;
  version: number;
  title?: string;
  status?: U3204ConversationStatus;
  pinned?: boolean;
}

/** Corps POST /U3204/conversations/reorder-pinned (BL-0493). */
export interface U3204ReorderPinnedBody {
  conversationIds: string[];
}

export interface U3204ConversationDeleteBody {
  id: string;
  version: number;
}

export type U3204ConversationSearchOptions = {
  status: U3204ConversationStatus;
  missionId?: string | null;
  searchText?: string;
};

export function u3204BuildConversationSearchPayload(
  statusOrOptions: U3204ConversationStatus | U3204ConversationSearchOptions,
): U2000SearchPayload {
  const options: U3204ConversationSearchOptions =
    typeof statusOrOptions === 'string' ? { status: statusOrOptions } : statusOrOptions;

  const criterias: Array<{ field: string; operator: string; value: string; type?: string }> = [];

  if (options.status === 'archived') {
    criterias.push({ field: 'status', operator: 'eq', value: 'archived', type: 'string' });
  }

  if (options.missionId) {
    criterias.push({ field: 'missionId', operator: 'eq', value: options.missionId, type: 'string' });
  }

  const trimmedSearch = options.searchText?.trim();
  if (trimmedSearch) {
    criterias.push({ field: 'searchText', operator: 'contains', value: trimmedSearch, type: 'string' });
  }

  return {
    pagination: { page: 1, size: 100 },
    filter: {
      criterias: criterias as any,
      fields: [
        { name: 'id' },
        { name: 'title' },
        { name: 'missionId' },
        { name: 'mission' },
        { name: 'status' },
        { name: 'version' },
        { name: 'lastModifiedAt' },
        { name: 'pinned' },
        { name: 'pinnedAt' },
        { name: 'pinnedOrder' },
      ],
      sortedFields: [{ name: 'lastModifiedAt', sortOrder: 'DESC' }],
    },
  };
}

export interface U3204SendMessageResult {
  conversationId: string;
  userMessageId: string;
  titleGenerationPending?: boolean;
}

/** Message affiché côté UI (optimiste ou serveur) */
export interface U3204DisplayMessage extends U3204Message {
  isOptimistic?: boolean;
  isError?: boolean;
  errorCode?: string;
}

export const U3204_ASSISTANT_PLACEHOLDER_ID = '__assistant_placeholder__';

/** Catégorie U3203 autorisée pour le sélecteur missions et la création de conversation chat (BL-0159). */
export const U3204_CHAT_MISSION_CATEGORY = 'assistant' as const satisfies U3203MissionCategory;

export function u3204BuildAssistantMissionsSearchPayload(): U2000SearchPayload {
  return {
    pagination: { page: 1, size: 100 },
    filter: {
      criterias: [
        { field: 'active', operator: 'eq', value: 'true', type: 'boolean' },
        {
          field: 'category',
          operator: 'eq',
          value: U3204_CHAT_MISSION_CATEGORY,
          type: 'string',
        },
      ],
      fields: [{ name: 'id' }, { name: 'code' }, { name: 'title' }],
      sortedFields: [{ name: 'code', sortOrder: 'ASC' }],
    },
  };
}

export const U3204_ERROR_CODE_EMPTY_MESSAGE = 'BU320405';
export const U3204_ERROR_CODE_UNSUPPORTED_MIME = 'BU320406';
export const U3204_ERROR_CODE_FILE_TOO_LARGE = 'BU320403';
export const U3204_ERROR_CODE_TOO_MANY_ATTACHMENTS = 'BU320404';
export const U3204_ERROR_CODE_STREAMING = 'BU320402';
export const U3204_ERROR_CODE_ORG_CONFIG = 'BU320208';
export const U3204_ERROR_CODE_MISSION_INACTIVE = 'BU320302';
export const U3204_ERROR_CODE_IMAGE_NOT_SUPPORTED = 'BU320220';
export const U3204_ERROR_CODE_PDF_NOT_SUPPORTED = 'BU320221';
export const U3204_ERROR_CODE_STREAMING_NOT_SUPPORTED = 'BU320222';
export const U3204_ERROR_CODE_EMPTY_TITLE = 'BU320407';
export const U3204_ERROR_CODE_INVALID_STATUS = 'BU320408';
export const U3204_ERROR_CODE_INVALID_MISSION_ID = 'BU320409';
export const U3204_ERROR_CODE_MISSION_CATEGORY_NOT_ALLOWED = 'BU320410';
export const U3204_ERROR_CODE_MISSION_NOT_IN_USER_GROUP = 'BU320411';
export const U3204_ERROR_CODE_PIN_NOT_ALLOWED = 'BU320413';
export const U3204_ERROR_CODE_USER_GROUP_REQUIRED = 'BU320414';
export const U3204_ERROR_CODE_USER_QUOTA_EXCEEDED = 'BU320412';
export const U3204_ERROR_CODE_GROUP_QUOTA_EXCEEDED = 'BU320415';
/** Liste reorder-pinned ≠ ensemble des épinglées (BL-0493). */
export const U3204_ERROR_CODE_PINNED_REORDER_INVALID = 'BU320419';

export function buildSendMessageFormData(content: string, files: File[]): FormData {
  const formData = new FormData();
  const trimmed = content.trim();
  if (trimmed) {
    formData.append('content', trimmed);
  }
  files.forEach((file) => {
    formData.append('files', file);
  });
  return formData;
}

export function validateAttachments(
  files: File[],
  initLU: Pick<U3204InitLU, 'allowedMimeTypes' | 'maxAttachmentBytes' | 'maxAttachmentsPerMessage'>,
  options?: {
    allowedMimeTypes?: string[];
    capabilities?: U3202ModelCapabilities | null;
  },
) {
  return u3204ValidateAttachments(files, initLU, options);
}

export function canSendMessage(content: string, files: File[]): boolean {
  return content.trim().length > 0 || files.length > 0;
}

export function createOptimisticUserMessage(content: string, files: File[], tempId: string): U3204DisplayMessage {
  return {
    id: tempId,
    role: 'user',
    content: content.trim(),
    isOptimistic: true,
    attachments: files.map((file, index) => ({
      id: `temp-${index}`,
      attachmentType: 'file',
      fileName: file.name,
      mimeType: file.type,
      fileSize: file.size,
    })),
  };
}

export function createAssistantPlaceholder(): U3204DisplayMessage {
  return {
    id: U3204_ASSISTANT_PLACEHOLDER_ID,
    role: 'assistant',
    content: '',
    generationStatus: 'pending',
    isOptimistic: true,
  };
}

export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.INITLU);
};

export const doGetAccessibleMissions = (axios: AxiosInstance) => {
  return axios.get<{ results: U3204MissionRef[] }>(LogicalUnitEndpoints.ACCESSIBLE_MISSIONS);
};

export const doGetQuotaStatus = (axios: AxiosInstance, missionId: string) => {
  return axios.get<{ result: U3204QuotaStatus }>(LogicalUnitEndpoints.QUOTA_STATUS, {
    params: { missionId },
  });
};

export const doSearchConversations = (axios: AxiosInstance, searchPayload: U2000SearchPayload) => {
  return axios.post(LogicalUnitEndpoints.SEARCH_CONVERSATIONS, searchPayload);
};

export const doCreateConversation = (axios: AxiosInstance, body: U3204CreateConversationBody) => {
  return axios.post(LogicalUnitEndpoints.CREATE_CONVERSATION, body);
};

export const doGetConversation = (axios: AxiosInstance, id: string | number) => {
  return axios.get(LogicalUnitEndpoints.GET_CONVERSATION(id));
};

export const doUpdateConversation = (
  axios: AxiosInstance,
  id: string | number,
  body: U3204ConversationUpdateBody,
) => {
  return axios.post(LogicalUnitEndpoints.UPDATE_CONVERSATION(id), body);
};

export const doDeleteConversation = (
  axios: AxiosInstance,
  id: string | number,
  body: U3204ConversationDeleteBody,
) => {
  return axios.post(LogicalUnitEndpoints.DELETE_CONVERSATION(id), body);
};

export const doReorderPinnedConversations = (axios: AxiosInstance, body: U3204ReorderPinnedBody) => {
  return axios.post(LogicalUnitEndpoints.REORDER_PINNED_CONVERSATIONS, body);
};

export const doSendMessage = (axios: AxiosInstance, conversationId: string | number, formData: FormData) => {
  return axios.post(LogicalUnitEndpoints.SEND_MESSAGE(conversationId), formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const doListProductHelpSources = (axios: AxiosInstance) => {
  return axios.get<{ results: U3204ProductHelpSource[] }>(LogicalUnitEndpoints.PRODUCT_HELP_SOURCES);
};

export const doReindexProductHelp = (axios: AxiosInstance, body: U3204ProductHelpReindexBody = {}) => {
  return axios.post<{ result?: U3204U2200Job } & U3204U2200Job>(LogicalUnitEndpoints.PRODUCT_HELP_REINDEX, body);
};

export const doGetU2200Job = (axios: AxiosInstance, jobId: string) => {
  return axios.get<{ result?: U3204U2200Job } & U3204U2200Job>(U2200_JOB_GET(jobId));
};

/** Extrait le job depuis une réponse U2200 (enveloppe `{ result }` ou job plat). */
export function u3204ParseU2200JobResponse(data: unknown): U3204U2200Job | null {
  if (!data || typeof data !== 'object') return null;
  const envelope = data as { result?: unknown; id?: unknown; status?: unknown };
  const raw = envelope.result && typeof envelope.result === 'object' ? envelope.result : data;
  if (!raw || typeof raw !== 'object') return null;
  const job = raw as U3204U2200Job;
  if (typeof job.id !== 'string' || typeof job.status !== 'string') return null;
  return job;
}

export function luHandleAppiError(
  t: TranslateFn,
  nameSpace: string,
  err: unknown,
  errorResult: U2000ApiErrorResult,
): U2000ApiErrorResult {
  const apiErr = err as { errorCode?: string };
  const code = apiErr?.errorCode;
  if (!code) return errorResult;

  const keyByCode: Record<string, string> = {
    [U3204_ERROR_CODE_EMPTY_MESSAGE]: 'errEmptyMessage',
    [U3204_ERROR_CODE_UNSUPPORTED_MIME]: 'errUnsupportedMime',
    [U3204_ERROR_CODE_FILE_TOO_LARGE]: 'errFileTooLarge',
    [U3204_ERROR_CODE_TOO_MANY_ATTACHMENTS]: 'errTooManyAttachments',
    [U3204_ERROR_CODE_STREAMING]: 'errStreaming',
    [U3204_ERROR_CODE_ORG_CONFIG]: 'errOrgConfig',
    [U3204_ERROR_CODE_MISSION_INACTIVE]: 'errMissionInactive',
    [U3204_ERROR_CODE_IMAGE_NOT_SUPPORTED]: 'errImageNotSupported',
    [U3204_ERROR_CODE_PDF_NOT_SUPPORTED]: 'errPdfNotSupported',
    [U3204_ERROR_CODE_STREAMING_NOT_SUPPORTED]: 'errStreamingNotSupported',
    [U3204_ERROR_CODE_EMPTY_TITLE]: 'errEmptyTitle',
    [U3204_ERROR_CODE_INVALID_STATUS]: 'errInvalidStatus',
    [U3204_ERROR_CODE_INVALID_MISSION_ID]: 'errInvalidMissionId',
    [U3204_ERROR_CODE_MISSION_CATEGORY_NOT_ALLOWED]: 'errMissionCategoryNotAllowed',
    [U3204_ERROR_CODE_MISSION_NOT_IN_USER_GROUP]: 'errMissionNotInUserGroup',
    [U3204_ERROR_CODE_PIN_NOT_ALLOWED]: 'errPinNotAllowed',
    [U3204_ERROR_CODE_USER_GROUP_REQUIRED]: 'errUserGroupRequired',
    [U3204_ERROR_CODE_USER_QUOTA_EXCEEDED]: 'errUserQuotaExceeded',
    [U3204_ERROR_CODE_GROUP_QUOTA_EXCEEDED]: 'errGroupQuotaExceeded',
    [U3204_ERROR_CODE_PINNED_REORDER_INVALID]: 'reorderPinnedError',
  };

  const messageKey = keyByCode[code];
  if (messageKey) {
    errorResult.otherErrors = {
      ...(errorResult.otherErrors ?? {}),
      api: t(messageKey, { ns: nameSpace }),
    };
  }

  return errorResult;
}
